import { useCallback, useEffect, useRef, useState } from 'react';

const MIN = 1;
const MAX = 8;
const WHEEL_STEP = 1.15;

const clamp = (v: number) => Math.min(MAX, Math.max(MIN, v));

/**
 * One image, full screen, zoomable (SLD-0004).
 *
 * The whole-sheet view is where the design is compared against the artwork,
 * and at fit-to-screen a 12 MP photograph is a postage stamp. Scroll wheel
 * zooms about the cursor, so the point being looked at stays put; the slider
 * and buttons are there for a trackpad or a touch screen; dragging pans.
 *
 * Only a click outside the image, Close, or Esc dismisses it. A click on the
 * image used to close the old lightbox, which is the same gesture as starting
 * a drag.
 */
export function ZoomViewer(
  { src, alt, caption, onClose }:
  { src: string; alt: string; caption: string; onClose: () => void },
) {
  const frame = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ scale: MIN, x: 0, y: 0 });
  // The wheel handler is attached once, so it reads the latest view from here.
  const current = useRef(view);
  current.current = view;
  const drag = useRef<{ x: number; y: number } | null>(null);
  const { scale } = view;

  /** Zoom to `next`, keeping the frame point (px, py) where it is. */
  const zoomAt = useCallback((next: number, px: number, py: number) => {
    const was = current.current;
    const to = clamp(next);
    setView(to === MIN
      ? { scale: MIN, x: 0, y: 0 }
      : { scale: to,
          x: px - (px - was.x) * (to / was.scale),
          y: py - (py - was.y) * (to / was.scale) });
  }, []);

  const zoomCentre = (next: number) => {
    const r = frame.current?.getBoundingClientRect();
    zoomAt(next, (r?.width ?? 0) / 2, (r?.height ?? 0) / 2);
  };

  // Not React's onWheel: it is passive, and the page must not scroll too.
  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const factor = e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
      zoomAt(current.current.scale * factor, e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="lightbox" role="dialog" aria-label={caption}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="zoom-toolbar">
        <button type="button" onClick={() => zoomCentre(scale / 1.5)}
                aria-label="Zoom out">−</button>
        <input type="range" min={MIN} max={MAX} step={0.1} value={scale}
               aria-label="Zoom"
               onChange={(e) => zoomCentre(Number(e.target.value))} />
        <button type="button" onClick={() => zoomCentre(scale * 1.5)}
                aria-label="Zoom in">+</button>
        <span className="zoom-level">{scale.toFixed(1)}×</span>
        <button type="button" onClick={() => zoomCentre(MIN)}>Fit</button>
        <button type="button" className="zoom-close" onClick={onClose}>
          Close
        </button>
      </div>
      <div ref={frame} className={`zoom-frame ${scale > MIN ? 'zoomed' : ''}`}
           onPointerDown={(e) => {
             if (scale === MIN) return;
             drag.current = { x: e.clientX - view.x, y: e.clientY - view.y };
             e.currentTarget.setPointerCapture(e.pointerId);
           }}
           onPointerMove={(e) => {
             if (drag.current) {
               const { x, y } = drag.current;
               setView((v) => ({ ...v, x: e.clientX - x, y: e.clientY - y }));
             }
           }}
           onPointerUp={() => { drag.current = null; }}
           onPointerCancel={() => { drag.current = null; }}>
        <img src={src} alt={alt} draggable={false}
             style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${scale})` }} />
      </div>
      <p className="zoom-caption">
        {caption} — scroll or use the slider to zoom, drag to move
      </p>
    </div>
  );
}
